import React from "react";
import classNames from "classnames/bind";
import PropTypes from "prop-types";
import { OrderedSet } from "immutable";

import Editor from "./editor/Editor";
import styles from "./styles/TriviaForm.sass";

const cx = classNames.bind(styles);

export class TriviaForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      category: props.category,
      bodySerialized: props.bodySerialized,
      bodyLength: 0,
      source: props.source,
      sourceErrors: this.getSourceErrors(props.source),
      inlineStyle: OrderedSet(),
      focus: false,
      submitted: false,
      shakeButton: false,
    };
  }

  onSubmit = () => {
    this.setState({ submitted: true });
    if (!(this.validBody() && this.validCategory() && this.validSource())) {
      this.shakeButton();
      return;
    }

    const { onSubmit, editing } = this.props;
    const { bodySerialized, category, source } = this.state;

    onSubmit({ bodySerialized, category, source })
      .then(() => {
        if (!editing) this.setState({ bodySerialized: "", source: "", sourceErrors: [], category: "", submitted: false });
      }).catch(({ source: sourceErrors = [] }) => {
        this.shakeButton();
        this.setState({ sourceErrors });
      });
  }

  onBodyChange = (bodySerialized, bodyLength) => {
    this.setState({
      bodySerialized,
      bodyLength,
    });
  }

  onSourceChange = ({ target }) => {
    const { value: source } = target;
    this.setState({ source, sourceErrors: this.getSourceErrors(source) });
  }

  getSourceErrors = (source = "") => {
    const { allowEmptySource } = this.props;
    const sourceErrors = [];
    if ((!allowEmptySource || source.length) && !source.match(this.sourceRegex)) {
      sourceErrors.push("Dies ist keine gültige URL (beginnt mit http:// oder https://) zu einer Quelle.");
    }
    return sourceErrors;
  }

  sourceRegex = "^https?://[^\"'\\s]+\\.[^\"'\\s]+$"

  shakeButton = () => {
    clearTimeout(this.timeout);
    this.setState({ shakeButton: false }, () => setTimeout(() => this.setState({ shakeButton: true }, () => {
      this.timeout = setTimeout(() => this.setState({ shakeButton: false }), 400);
    })));
  }

  validBody = () => {
    const { bodyLength } = this.state;
    return bodyLength >= 10 && bodyLength <= 500;
  }

  validCategory = () => {
    const { categories } = this.props;
    const { category } = this.state;
    return categories.indexOf(category) >= 0;
  }

  validSource = () => {
    const { sourceErrors } = this.state;
    return !sourceErrors.length;
  }

  render() {
    const { body, cancelable, categories, editing, onCancel } = this.props;
    const { bodyLength, bodySerialized, category: activeCategory, focus, inlineStyle, shakeButton, source, sourceErrors, submitted } = this.state;
    const submitText = editing ? "Trivia speichern" : "speichern und veröffentlichen";

    return (
      <div className={cx("wrapper")}>
        <div className={cx("inputWrapper", { isFullwidth: editing })}>
          <div className={cx("bodyWrapper")}>
            <div className={cx("body", { hasError: submitted && !this.validBody() })}>
              <Editor
                body={body}
                bodySerialized={bodySerialized}
                placeholder="Mit @ kannst du Filme, Serien oder Personen deiner Trivia hinzufügen."
                ref={(editor) => { if (this.editor == null && editor != null) this.editor = editor; }}
                onBodyChange={this.onBodyChange}
                onInlineStyleChange={(newInlineStyle) => { this.setState({ inlineStyle: newInlineStyle }); }}
                onFocusChange={(newFocus = true) => { this.setState({ focus: newFocus }); }}
              />
            </div>
          </div>
          <div className={cx("error", { isActive: bodyLength < 10 && submitted })}>
            <div className={cx("errorText")}>Die Trivia ist zu kurz. Bitte schreibe ausführlicher.</div>
          </div>
          <div className={cx("error", { isActive: bodyLength > 500 && submitted })}>
            <div className={cx("errorText")}>Das Trivia ist zu lang. Bitte fasse dich kürzer.</div>
          </div>
          <div className={cx("meta", { isInline: editing })}>
            <div className={cx("controlsWrapper")}>
              <div className={cx("controls")}>
                <div className={cx("categoryWrapper")}>
                  <select
                    className={cx("category", { hasError: submitted && !this.validCategory() })}
                    value={activeCategory}
                    onChange={({ target }) => { this.setState({ category: target.value }); }}
                    required
                  >
                    <option key="placeholder" value="" disabled>Kategorie</option>
                    {categories.map(category => <option key={category} value={category}>{category}</option>)}
                  </select>
                </div>
                <div className={cx("spoilerWrapper")}>
                  <button
                    className={cx("spoiler", { isActive: inlineStyle.has("SPOILER") && focus })}
                    onClick={() => { this.editor.toggleSpoiler(); }}
                    type="button"
                  >
                    Spoiler
                  </button>
                </div>
              </div>
              <div className={cx("error", { isActive: submitted && !this.validCategory() })}>
                <div className={cx("errorText")}>Bitte wähle eine Kategorie aus.</div>
              </div>
            </div>
            <div className={cx("sourceOuterWrapper")}>
              <div className={cx("sourceInnerWrapper")}>
                <input
                  className={cx("source", { hasError: submitted && !this.validSource() })}
                  type="url"
                  pattern={this.sourceRegex}
                  placeholder="Deine Quelle (URL)"
                  onChange={this.onSourceChange}
                  value={source}
                />
              </div>
              <div className={cx("error", { isActive: submitted && !this.validSource() })}>
                <div className={cx("errorText")}>{sourceErrors.join(" ")}</div>
              </div>
            </div>
          </div>
          <div className={cx("buttons")}>
            <button
              className={cx("button", "submit", "is-primary", "is-fullwidth", { shake: shakeButton })}
              onClick={this.onSubmit}
              disabled={this.props.isUpdating}
              type="submit"
            >
              {submitText}
            </button>
            {cancelable && (
              <button
                className={cx("button", "cancel")}
                onClick={onCancel}
                type="button"
              >
                abbrechen
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

TriviaForm.propTypes = {
  body: PropTypes.string,
  bodySerialized: PropTypes.string,
  cancelable: PropTypes.bool,
  categories: PropTypes.array,
  category: PropTypes.string,
  editing: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  source: PropTypes.string,
  allowEmptySource: PropTypes.bool,
  isUpdating: PropTypes.bool,
};

const noOp = () => { };
TriviaForm.defaultProps = {
  body: undefined,
  bodySerialized: undefined,
  cancelable: false,
  categories: [],
  category: "",
  editing: false,
  onCancel: noOp,
  onSubmit: noOp,
  source: "",
  allowEmptySource: false,
  isUpdating: false,
};

export default TriviaForm;
